import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { versionListResult, wsChannel } from '@xdocs/schemas';
import type { DocumentVersion, ErrorAlert } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

const PAGE_SIZE = 20;

export interface UseVersionHistoryResult {
  /** Newest-first (server default sort/dir). */
  versions: readonly DocumentVersion[];
  isLoading: boolean;
  isError: boolean;
  error: ErrorAlert | null;
  refetch: () => void;
  hasMore: boolean;
  loadMore: () => void;
  isLoadingMore: boolean;
  loadMoreError: string | null;
  /** F67 "restore as new version": there is no dedicated `version.restore`
   * op in the frozen M3 contract — restoring is composed from the two ops
   * that ARE frozen (`version.get` + `editor.save`), matching the immutable-
   * DAG model exactly: it fetches the target version's content and appends
   * it as a brand-new version branching from the document's CURRENT head,
   * never mutating history (F3 AC precedent: "the original is preserved as
   * a separate version"). */
  restoreAsNew: (targetVersionId: string, currentBaseVersionId: string) => Promise<DocumentVersion>;
  isRestoring: boolean;
}

/**
 * F67/F65's version-history sidebar (§28.1) — server-side paginated
 * `version.list` (E6/§5.9: no fetch-all-then-slice), "Load more" cursor-
 * append pattern mirroring `useJobSnapshot`'s `loadEarlierLogs`. Live via
 * `document:{documentId}` (a new version lands from generate/expand/cut/
 * difficulty/translate/edit/restyle) — refetches the first page on push so
 * the list stays current without a bespoke channel.
 */
export function useVersionHistory(documentId: string): UseVersionHistoryResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const enabled = connectionState === 'connected';

  const queryKey = ['versions', documentId] as const;

  const query = useQuery<readonly DocumentVersion[], ErrorAlert>({
    queryKey,
    // `versionListResult.parse` and not the raw payload: `requestTyped` is
    // compile-time-only, so `createdAt` arrives as a raw ISO STRING despite
    // the `Date` type — `VersionHistorySidebar`'s `formatDate(version.
    // createdAt)` then throws `RangeError: Invalid time value` and (with no
    // error boundary) white-screens the whole editor. Parse at the trust
    // boundary coerces + validates (same fix as `useJobSnapshot`).
    queryFn: () =>
      requestTyped(wsClient, 'version.list', { documentId, page: 1, pageSize: PAGE_SIZE, sort: 'createdAt', dir: 'desc' }).then(
        (result) => versionListResult.parse(result).rows,
      ),
    enabled,
  });

  useEffect(() => {
    const channel = wsChannel.document(documentId);
    return wsClient.subscribe(channel, () => {
      void queryClient.invalidateQueries({ queryKey });
      setNextPage(2);
      setHasMore(true);
    });
    // `queryKey` is derived from `documentId`, already a dependency.
  }, [wsClient, queryClient, documentId]);

  const [extra, setExtra] = useState<DocumentVersion[]>([]);
  const [nextPage, setNextPage] = useState(2);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  useEffect(() => {
    setExtra([]);
    setNextPage(2);
    setHasMore(true);
    setLoadMoreError(null);
  }, [documentId]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    setLoadMoreError(null);
    requestTyped(wsClient, 'version.list', {
      documentId,
      page: nextPage,
      pageSize: PAGE_SIZE,
      sort: 'createdAt',
      dir: 'desc',
    })
      .then((raw) => {
        // Same runtime-coercion rationale as the first-page queryFn above.
        const result = versionListResult.parse(raw);
        setExtra((prev) => [...prev, ...result.rows]);
        setNextPage((p) => p + 1);
        setHasMore(result.rows.length === PAGE_SIZE);
      })
      .catch((err: unknown) => {
        const alert = err as ErrorAlert;
        setLoadMoreError(alert.message ?? 'errors:code.INTERNAL');
      })
      .finally(() => setIsLoadingMore(false));
  }, [wsClient, documentId, nextPage, isLoadingMore, hasMore]);

  const [isRestoring, setIsRestoring] = useState(false);
  const restoreAsNew = useCallback(
    async (targetVersionId: string, currentBaseVersionId: string): Promise<DocumentVersion> => {
      setIsRestoring(true);
      try {
        const target = await requestTyped(wsClient, 'version.get', { documentId, versionId: targetVersionId });
        return await requestTyped(wsClient, 'editor.save', {
          documentId,
          baseVersionId: currentBaseVersionId,
          content: target.contentJsonb,
          clientMutationId: crypto.randomUUID(),
        });
      } finally {
        setIsRestoring(false);
      }
    },
    [wsClient, documentId],
  );

  const seen = new Set<string>();
  const versions: DocumentVersion[] = [];
  for (const v of [...(query.data ?? []), ...extra]) {
    if (seen.has(v.id)) continue;
    seen.add(v.id);
    versions.push(v);
  }

  return {
    versions,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.isError ? query.error : null,
    refetch: () => void query.refetch(),
    hasMore,
    loadMore,
    isLoadingMore,
    loadMoreError,
    restoreAsNew,
    isRestoring,
  };
}
